/* eslint-disable camelcase */
import type {
    ExtraType,
    FinanceInfoDisclaimerType,
    ServiceProductType,
    WheelType,
} from "../../../common-deprecated/types/CommonTypes";

export enum SectionTypeEnum {
    FinanceProduct = "financeProduct",
    Duration = "duration",
    Mileage = "mileage",
    Downpayment = "downpayment",
    Insurance = "insurance",
    ServiceContract = "serviceContract",
    ServiceProducts = "serviceProducts",
    Balloon = "balloon",
    ResidualValue = "residualValue",
    HomeCharging = "homeCharging",
    HomeChargingInterested = "homeChargingInterested",
    OptionalPacks = "optionalPacks",
    OptionalWinterWheels = "optionalWinterWheels",
    OptionalAccessories = "optionalAccessories",
}

// Used for legal text and layout of legal text tables.
export type LegalTextResponseType = {
    Legal_Text: {
        Legal_Text_Value: string;
        Promo_Legal_Text_Value?: string;
        Product_Name?: string;
        Calculations?: {
            Calculation: {
                Calculation_Name: string;
                Calculation_Value: string;
                Tooltip: string;
            }[];
        };
    };
};

export type LegalBannerDisclaimerDataType = {
    finance: Record<string, string>[];
    minMax: { min: FinanceInfoDisclaimerType["table"]; max?: FinanceInfoDisclaimerType["table"] };
    disclaimer: string;
    tableOrientation?: LegalBannerTableOrientation;
};

export type LegalBannerDisclaimerPayloadType = {
    title: string;
    dictionary: Record<string, string>;
    finance: {
        image: {
            mobile?: {
                imagePath?: string;
                altText?: string;
            };
            desktop?: {
                imagePath?: string;
                altText?: string;
            };
        };
        finance: Record<string, string>[];
        text: string;
    };
    tableOrientation?: LegalBannerTableOrientation;
};

export enum LegalBannerTableOrientation {
    vertical = "vertical",
    horizontal = "horizontal",
}

export type FinanceInputsType = Partial<{
    [SectionTypeEnum.FinanceProduct]: string;
    [SectionTypeEnum.Duration]: number;
    [SectionTypeEnum.Mileage]: number;
    [SectionTypeEnum.Downpayment]: number;
    [SectionTypeEnum.Insurance]: boolean;
    [SectionTypeEnum.Balloon]: number;
    [SectionTypeEnum.ResidualValue]: number;
    [SectionTypeEnum.ServiceContract]: boolean;
}>;

export type ListType<T = number> = {
    default?: { value: T };
    list: T[];
    isDisabled?: boolean;
};

export type ToggleType = ListType<boolean> & { isDisabled?: boolean };

export type RangeType = {
    min: number;
    max: number;
    step: number;
};

// TypeGuards for ListType, ToggleType & RangeType
type InputTypes = ListType | ToggleType | RangeType | undefined;
export const isRangeType = (data: InputTypes): data is RangeType => {
    return !!data && "min" in data && "max" in data && "step" in data;
};
const isGenericListType = (data: InputTypes): data is ListType | ToggleType => {
    return !!data && "list" in data && Array.isArray(data.list);
};
export const isListType = (data: InputTypes): data is ListType => {
    return isGenericListType(data) && !isToggleType(data);
};
export const isToggleType = (data: InputTypes): data is ToggleType => {
    return (
        isGenericListType(data) &&
        (data.list as (number | boolean)[]).every((val) => typeof val === "boolean") &&
        (data.list as boolean[]).includes(true) &&
        (data.list as boolean[]).includes(false)
    );
};

export type CalculationType = {
    downpayment?: number;
    balloon?: number;
    residualValue?: number;
    duration: number;
    mileage: number;
    monthlyPayment: number;
    legalText: string;
    financeQuoteId: string;
    apr?: number; // annual percentage rate
    interestRate?: number;
    effectiveInterestRate?: number;
    insurance?: boolean;
    serviceContract?: boolean;
};

export type FinanceProductType = {
    product: string;
    productName: string;
};

export type FinanceConfigType = Partial<{
    productConfiguration: ListType<FinanceProductType>;
    downpaymentConfiguration: RangeType;
    balloonConfiguration: RangeType;
    residualValueConfiguration: RangeType;
    mileageConfiguration: ListType;
    durationConfiguration: ListType;
    insuranceConfiguration: ToggleType;
    serviceContractConfiguration: ToggleType;
}>;

export type FinancePackConfigurablesType = ExtraType | WheelType | ServiceProductType;
